/* eslint-disable react/jsx-props-no-spreading */
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDrag } from '@use-gesture/react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useRef, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';

import { useTheme, useThemeClassName } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';

import styles from '../../../styles/questions/slider.module.scss';

const propTypes = {
    className: PropTypes.string,
    question: AppPropTypes.question,
    answer: AppPropTypes.answer,
    defaultValue: PropTypes.number,
    defaultMin: PropTypes.number,
    defaultMax: PropTypes.number,
    onAnswer: PropTypes.func,
};

const defaultProps = {
    className: null,
    question: null,
    answer: null,
    defaultValue: 0,
    defaultMin: 0,
    defaultMax: 15,
    onAnswer: null,
};

function SliderQuestion({
    className,
    question,
    answer,
    defaultValue,
    defaultMin,
    defaultMax,
    onAnswer,
}) {
    const { name: theme = null } = useTheme();
    const themeClassName = useThemeClassName(styles);
    const {
        initialValue = defaultValue,
        min = defaultMin,
        max = defaultMax,
        validAnswer = null,
    } = question || {};
    const { value = initialValue } = answer || {};

    const hasValidAnswer = validAnswer !== null;
    const userIsRight = hasValidAnswer && validAnswer === value;
    const userIsWrong = hasValidAnswer && validAnswer !== value;

    const onMinusClick = useCallback(() => {
        if (onAnswer !== null) {
            onAnswer(Math.max(0, value - 1));
        }
    }, [onAnswer, value]);

    const onPlusClick = useCallback(() => {
        if (onAnswer !== null) {
            onAnswer(Math.max(0, value + 1));
        }
    }, [onAnswer, value]);

    const rangeInnerContent = useRef(null);
    const bind = useDrag(({ xy: [x] }) => {
        const { width, left } = rangeInnerContent.current.getBoundingClientRect();
        const ratio = (x - left) / width;
        const rangeFloat = min + ratio * (max - min);
        const rangeInt = Math.max(min, Math.min(max, Math.round(rangeFloat)));

        if (value !== rangeInt) {
            if (onAnswer !== null) {
                onAnswer(rangeInt);
            }
        }
    });

    const thumbLeft = useMemo(
        () => `${((Math.max(min, Math.min(max, value)) - min) * 100) / (max - min)}%`,
        [min, max, value],
    );

    return (
        <div
            className={classNames([
                styles.container,
                themeClassName,
                {
                    [styles.userIsRight]: userIsRight,
                    [styles.userIsWrong]: userIsWrong,
                    [className]: className !== null,
                },
            ])}
        >
            <div className={styles.slider}>
                <div className={styles.line} />
                <Button className={styles.button} onClick={onMinusClick}>
                    <FontAwesomeIcon icon={faMinus} />
                </Button>
                <Button className={styles.range}>
                    <span className={styles.rangeInner} {...bind()}>
                        <span className={styles.rangeInnerContent} ref={rangeInnerContent}>
                            <span
                                className={styles.thumb}
                                style={{
                                    left: thumbLeft,
                                }}
                            >
                                <span className={styles.thumbInner}>
                                    {answer !== null ? value : null}
                                </span>
                            </span>
                        </span>
                    </span>
                </Button>
                <Button className={styles.button} onClick={onPlusClick}>
                    <FontAwesomeIcon icon={faPlus} />
                </Button>
            </div>
            <div className={styles.validatedContainer}>
                {userIsWrong ? (
                    <FormattedMessage
                        defaultMessage="The right answer was {answer}."
                        description="Question right answer label"
                        values={{ answer: `${validAnswer}` }}
                    />
                ) : null}
            </div>
        </div>
    );
}

SliderQuestion.propTypes = propTypes;
SliderQuestion.defaultProps = defaultProps;

export default SliderQuestion;
